/**
 * The kind of content a sub-viewport holds, which decides its one publisher: the
 * 3D/2D offscreen pass owns `'3d'` and `'2d'` (with the Controls beside that content),
 * `ControlRasterPass.tsx` owns `'dom'`, and nothing publishes `'empty'`. Both write the same `ViewportTextureRegistry`
 * key, so the split is decided here alone.
 */
import { is2DUIType } from '../../../r3f/controls/has2DUIContent.js';
import { nodeComponentRegistry } from '../../../r3f/NodeComponentRegistry.js';
import { liveTreeOf, } from '../../../r3f/liveSceneTree.js';
import { compositeCallPrefix } from '../../../godot/variant/variantParser.js';
/**
 * The `i`-suffixed spellings are listed: `Vector2i` is as 2D as `Vector2`, and it
 * is what Godot writes for `frame_coords`, `region_rect` and `size`. Without them
 * such an override matches neither list and falls through to the 3D publisher.
 */
const TWO_D_COMPOSITE = compositeCallPrefix('Vector2', 'Vector2i', 'Transform2D', 'Rect2', 'Rect2i');
const THREE_D_COMPOSITE = compositeCallPrefix('Vector3', 'Vector3i', 'Transform3D', 'Basis', 'Quaternion', 'AABB');
/**
 * CanvasItem-only property names, each on `CanvasItem` or `Node2D` and on no Node3D
 * class. One among an instance node's own overrides names its sub-scene's world
 * before the sub-scene loads.
 */
const CANVAS_ITEM_ONLY_PROPERTIES = new Set([
    'modulate',
    'self_modulate',
    'show_behind_parent',
    'clip_children',
    'texture_filter',
    'texture_repeat',
    'light_mask',
    'z_index',
    'z_as_relative',
    'y_sort_enabled',
    'skew',
]);
/**
 * Which world an instance node's own overrides name, or null when they name
 * neither. The parser keeps them in `rawProperties`, since the node has no type
 * yet. A transform constructor decides alone: `position = Vector2(…)` cannot be a
 * Node3D, and `Transform3D(…)` cannot be a CanvasItem.
 */
function instanceOverrideKind(node) {
    const raw = node.rawProperties;
    if (!raw)
        return null;
    for (const [key, value] of Object.entries(raw)) {
        if (CANVAS_ITEM_ONLY_PROPERTIES.has(key))
            return '2d';
        if (TWO_D_COMPOSITE.test(value))
            return '2d';
        if (THREE_D_COMPOSITE.test(value))
            return '3d';
    }
    return null;
}
/**
 * Classify a sub-viewport's subtree. Precedence is 3D > 2D > DOM, and the world pass draws one
 * kind. Controls and 2D items beside a 3D world draw over it in a canvas pass, as Godot's canvas
 * items draw after its 3D render (`renderer_viewport.cpp:380-715`). A nested sub-viewport draws
 * into its own target, so it is not descended into.
 */
export function viewportContent(node) {
    let sawCanvasItem = false;
    let sawDom = false;
    let sawUntypedInstance = false;
    // Every child is visited, so `sawDom` is complete when a 3D node comes first.
    const hasNode3DContent = (nodes) => {
        let found = false;
        for (const child of nodes) {
            if (isNode3DContent(child))
                found = true;
        }
        return found;
    };
    const isNode3DContent = (child) => {
        if (child.type === 'SubViewport')
            return false;
        // Godot's question (`is2DUIType`), not the component registry's: a Control
        // with no component of its own, such as `ProgressBar`, still gets a pass.
        if (is2DUIType(child.type)) {
            sawDom = true;
            return hasNode3DContent(child.children);
        }
        if (nodeComponentRegistry.isCanvasItem(child.type)) {
            sawCanvasItem = true;
            return hasNode3DContent(child.children);
        }
        if (child.instance) {
            const override = instanceOverrideKind(child);
            if (override === '2d') {
                sawCanvasItem = true;
                return false;
            }
            if (override === '3d')
                return true;
            sawUntypedInstance = true;
            return false;
        }
        // A workspace-neutral container (`Node`, `Timer`, …) may hold either kind,
        // so descend. `Node` is registered with `container: true`, so `get()` alone
        // would read every plain-Node-rooted sub-scene as 3D.
        if (nodeComponentRegistry.isContainer(child.type)) {
            return hasNode3DContent(child.children);
        }
        // A registered non-container type is Node3D content by elimination:
        // CanvasItems and Controls were both matched above.
        if (nodeComponentRegistry.get(child.type))
            return true;
        return hasNode3DContent(child.children);
    };
    const found = (kind) => ({
        kind,
        holdsControls: sawDom,
        holdsCanvasItems: sawCanvasItem,
    });
    if (hasNode3DContent(node.children))
        return found('3d');
    if (sawCanvasItem)
        return found('2d');
    // Godot's viewport demos instance 3D sub-scenes, so an untyped instance reads as
    // 3D, but only when nothing decisive was found: anything typed, or an instance
    // whose overrides name a world, outranks it.
    if (sawUntypedInstance)
        return found('3d');
    return found(sawDom ? 'dom' : 'empty');
}
export function viewportContentKind(node) {
    return viewportContent(node).kind;
}
/** The children below `from` (the roots without it) with their instances resolved. */
function resolvedChildren(tree, from) {
    return tree.fold(undefined, ({ raw, step }, _, descend) => {
        if (raw.type === 'SubViewport')
            return raw;
        return { ...step.node, children: descend() };
    }, from);
}
/**
 * The sub-viewport's subtree with its instances resolved, the input
 * `viewportContentKind` wants: a parsed `instance=` child is a childless `Node`
 * whatever its world. `viewport` is the sub-viewport's link in `tree`, which gives each
 * instance below it its step. A nested sub-viewport is returned untouched.
 */
export function resolveViewportSubtree(tree, viewport) {
    return { ...viewport.step.node, children: resolvedChildren(tree, viewport) };
}
/**
 * {@link resolveViewportSubtree} for a sub-viewport that no walk of the live tree has visited: it
 * reads its own tree of the children. `node` is already the node as shown, so it takes no step of
 * its own, and the scope declares an empty SubResource pool because this resolution reads no id.
 * `SceneScope` says why the two pools travel together.
 */
export function resolveStandaloneViewportSubtree(node, externalResources, sceneCache) {
    const ctx = { externalResources, internalResources: [], sceneCache };
    return { ...node, children: resolvedChildren(liveTreeOf(node.children, ctx)) };
}
//# sourceMappingURL=viewportContent.js.map